import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button,
  Snackbar,
  IconButton,
  Grid,
  Paper,
  Chip,
  Divider,
  useTheme,
  alpha,
  Tooltip,
  Fade,
  Skeleton,
  Zoom,
  styled,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line } from 'recharts';
import { tenantTokenService } from '../../../services/skillcoinService';

// Styled components for modern UI
const GradientCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  overflow: 'visible',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-4px)'
  }
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: theme.palette.background.paper,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '16px 16px 0 0'
  },
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
  }
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-4px)'
  }
}));

const PeriodSelector = styled(FormControl)(({ theme }) => ({
  minWidth: 140,
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    background: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }
  }
}));

const StatButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  padding: '8px 16px',
  boxShadow: 'none',
  fontWeight: 600,
  '&:hover': {
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)'
  }
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.06)'
}));

// Simulated fallback/placeholder data with more realistic values
const FALLBACK_DATA = {
  transactionStats: [
    { _id: 'earn', totalAmount: 24360, count: 342 },
    { _id: 'spend', totalAmount: 18750, count: 241 },
    { _id: 'transfer_in', totalAmount: 12480, count: 108 },
    { _id: 'transfer_out', totalAmount: 8920, count: 94 }
  ],
  activityStats: [
    { _id: 'Course Completion', totalAmount: 9680, count: 121 },
    { _id: 'Workshop Attendance', totalAmount: 7340, count: 89 },
    { _id: 'Content Creation', totalAmount: 4550, count: 43 },
    { _id: 'Community Support', totalAmount: 3120, count: 76 },
    { _id: 'Mentoring', totalAmount: 6450, count: 58 }
  ],
  topEarners: [
    { memberName: 'Jennifer K.', balance: 4850, totalEarned: 6230, totalSpent: 1380 },
    { memberName: 'Michael T.', balance: 3780, totalEarned: 5150, totalSpent: 1370 },
    { memberName: 'Sarah D.', balance: 3640, totalEarned: 4200, totalSpent: 560 },
    { memberName: 'Robert L.', balance: 3120, totalEarned: 3800, totalSpent: 680 },
    { memberName: 'Emma P.', balance: 2960, totalEarned: 3420, totalSpent: 460 },
    { memberName: 'David W.', balance: 2740, totalEarned: 3980, totalSpent: 1240 },
    { memberName: 'Lisa M.', balance: 2580, totalEarned: 2980, totalSpent: 400 },
    { memberName: 'Daniel C.', balance: 2380, totalEarned: 3120, totalSpent: 740 },
    { memberName: 'Jessica B.', balance: 2260, totalEarned: 2800, totalSpent: 540 },
    { memberName: 'Kevin S.', balance: 2110, totalEarned: 2650, totalSpent: 540 }
  ],
  tokenStats: {
    totalSupply: 500000,
    circulating: 162500,
    holders: 738,
    avgBalance: 220.2
  },
  timeSeriesStats: {
    transactionVolume: [
      { date: '2025-03-21', volume: 2480 },
      { date: '2025-03-28', volume: 3240 },
      { date: '2025-04-04', volume: 4120 },
      { date: '2025-04-11', volume: 3850 },
      { date: '2025-04-18', volume: 4760 }
    ],
    userGrowth: [
      { date: '2025-03-21', users: 620 },
      { date: '2025-03-28', users: 654 },
      { date: '2025-04-04', users: 681 },
      { date: '2025-04-11', users: 712 },
      { date: '2025-04-18', users: 738 }
    ]
  }
};

// Rich color palette for data visualization
const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', 
  '#6366F1', '#84CC16'
];

const TokenStatistics = ({ tokenId }) => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');
  const [retryCount, setRetryCount] = useState(0);
  const [autoRetry, setAutoRetry] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [usePlaceholder, setUsePlaceholder] = useState(false);
  const autoRetryTimeoutRef = useRef(null);
  const [loadingTime, setLoadingTime] = useState(0);
  const maxRetries = 3;
  const retryDelay = 5000; // 5 seconds
  const requestTimeout = 10000; // 10 seconds

  useEffect(() => {
    if (tokenId) {
      fetchStatistics();
    } else {
      // If no tokenId, use placeholder data after a short delay
      setLoading(true);
      const timer = setTimeout(() => {
        setStats(FALLBACK_DATA);
        setLoading(false);
        setUsePlaceholder(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [tokenId, period, retryCount]);

  useEffect(() => {
    if (autoRetry && retryCount < maxRetries) {
      autoRetryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prevCount => prevCount + 1);
      }, retryDelay);
    } else if (retryCount >= maxRetries) {
      setAutoRetry(false);
      setToastMessage('Auto-retry limit reached. Showing sample visualization data.');
      setToastOpen(true);
      // Use placeholder data after maximum retries
      setStats(FALLBACK_DATA);
      setUsePlaceholder(true);
      setError(null);
      setLoading(false);
    }
    return () => clearTimeout(autoRetryTimeoutRef.current);
  }, [autoRetry, retryCount]);

  // Loading timer effect
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
    } else {
      setLoadingTime(0);
    }
    return () => clearInterval(timer);
  }, [loading]);

  // Effect to show sample data if loading takes too long
  useEffect(() => {
    if (loadingTime > 8 && loading) {
      setToastMessage('Taking longer than expected. Would you like to see sample data while waiting?');
      setToastOpen(true);
    }
  }, [loadingTime, loading]);

  const fetchStatistics = async () => {
    if (usePlaceholder) {
      // If we're using placeholder data, don't try to fetch
      setUsePlaceholder(false);
    }
    
    setLoading(true);
    setError(null);
    setLoadingTime(0);
    
    try {
      // Create an AbortController to handle timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), requestTimeout);
      
      // Attempt to get data from API with timeout
      const data = await tenantTokenService.getTokenStatistics(period, controller.signal);
      
      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
      
      // Enhance the data with token stats if not present in the API response
      let enhancedData = data;
      
      // Check for empty or invalid response
      if (!data || Object.keys(data).length === 0) {
        console.warn('Empty or invalid response received');
        setStats(FALLBACK_DATA);
        setUsePlaceholder(true);
        setToastMessage('No data available from server. Showing sample visualization.');
        setToastOpen(true);
      } else {
        // If the API doesn't provide token stats, add simulated ones
        if (!data.tokenStats) {
          enhancedData = {
            ...data,
            tokenStats: {
              totalSupply: 500000,
              circulating: data.transactionStats ? 
                data.transactionStats.reduce((acc, stat) => acc + (stat.totalAmount || 0), 0) : 
                150000,
              holders: data.topEarners ? data.topEarners.length * 12 : 700,
              avgBalance: data.topEarners ? 
                (data.topEarners.reduce((acc, earner) => acc + (earner.balance || 0), 0) / data.topEarners.length).toFixed(1) : 
                210.5
            }
          };
        }
        
        // If the API doesn't provide time series data, add simulated ones
        if (!data.timeSeriesStats) {
          enhancedData = {
            ...enhancedData,
            timeSeriesStats: FALLBACK_DATA.timeSeriesStats
          };
        }
        
        setStats(enhancedData);
        setUsePlaceholder(false);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      
      // Provide more specific error messages based on error type
      if (err.name === 'AbortError') {
        setError('Request timed out. The server is taking too long to respond.');
      } else if (err.response) {
        if (err.response.status === 500) {
          setError('The server encountered an error. This could be temporary - please try again later.');
        } else if (err.response.status === 404) {
          setError('Statistics data not found. You may need to set up your token first.');
        } else {
          setError(`Error (${err.response.status}): ${err.response.data?.message || 'Failed to load statistics'}`);
        }
      } else if (err.request) {
        setError('No response from server. Please check your connection and try again.');
      } else {
        setError(err.message || 'An unexpected error occurred while fetching statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prevCount => prevCount + 1);
  };

  const handleAutoRetryToggle = () => {
    setAutoRetry(prev => !prev);
  };

  const handleUsePlaceholder = () => {
    setStats(FALLBACK_DATA);
    setUsePlaceholder(true);
    setError(null);
    setToastMessage('Showing sample visualization data.');
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const formatTransactionData = () => {
    if (!stats || !stats.transactionStats) return [];
    
    // Filter out earned transactions with source wallet
    const filteredStats = stats.transactionStats.map(stat => {
      if (stat._id === 'earn') {
        // Create a copy with filtered transactions if metadata is available
        return {
          ...stat,
          // Apply filtering only if we have transaction details with metadata
          filteredAmount: stat.transactions ? 
            stat.transactions
              .filter(tx => !(
                (tx.metadata && tx.metadata.source && 
                 (tx.metadata.source.toLowerCase() === 'wallet' || 
                  tx.metadata.source.toLowerCase().includes('wallet'))) ||
                (tx.source && 
                 (tx.source.toLowerCase() === 'wallet' || 
                  tx.source.toLowerCase().includes('wallet')))
              ))
              .reduce((sum, tx) => sum + (tx.amount || 0), 0) : 
            stat.totalAmount // Use original amount if no transaction details
        };
      }
      return stat;
    });
    
    return filteredStats.map(stat => ({
      name: formatTransactionType(stat._id),
      // Use filteredAmount for earned transactions if available
      value: stat._id === 'earn' && stat.filteredAmount !== undefined ? 
        stat.filteredAmount : stat.totalAmount,
      count: stat.count
    }));
  };

  const formatActivityData = () => {
    if (!stats || !stats.activityStats) return [];
    return stats.activityStats.map(stat => ({
      name: stat._id || 'Unknown',
      value: stat.totalAmount,
      count: stat.count
    }));
  };

  const formatTransactionType = (type) => {
    switch (type) {
      case 'earn': return 'Earned';
      case 'spend': return 'Spent';
      case 'transfer_out': return 'Sent';
      case 'transfer_in': return 'Received';
      case 'exchange_out': return 'Exchanged Out';
      case 'exchange_in': return 'Exchanged In';
      case 'refund': return 'Refunded';
      case 'adjustment': return 'Adjustments';
      case 'fee': return 'Fees';
      default: return type;
    }
  };
  
  // Format large numbers with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Calculate percentage change for metrics
  const getPercentChange = (current, previous) => {
    if (!previous) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Render placeholder when no data is available
  const renderNoDataPlaceholder = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3
        }}
      >
        <TimelineIcon fontSize="large" color="primary" />
      </Box>
      <Typography variant="h6" gutterBottom>
        No Statistics Data Available
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
        This could happen if your token is new or there has been no activity during this time period.
        You can try changing the time period or view sample data.
      </Typography>
      <StatButton 
        variant="contained" 
        color="primary" 
        onClick={handleUsePlaceholder}
        sx={{ mt: 2 }}
        startIcon={<TimelineIcon />}
      >
        Show Sample Visualization
      </StatButton>
    </Box>
  );

  // Render the loading state with a better visual
  const renderLoadingState = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 5 }}>
      <CircularProgress size={50} thickness={4} variant="indeterminate" />
      <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
        Loading token statistics...
      </Typography>
      {loadingTime > 5 && (
        <Fade in={loadingTime > 5}>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleUsePlaceholder}
              size="small"
              sx={{ mx: 1 }}
            >
              Show Sample Data
            </Button>
          </Box>
        </Fade>
      )}
    </Box>
  );

  // Render skeleton/placeholder charts while loading
  const renderSkeletonCharts = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <MetricCard>
          <Skeleton variant="rectangular" height={120} />
        </MetricCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <MetricCard>
          <Skeleton variant="rectangular" height={120} />
        </MetricCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <MetricCard>
          <Skeleton variant="rectangular" height={120} />
        </MetricCard>
      </Grid>
      <Grid item xs={12}>
        <ChartContainer>
          <Skeleton variant="rectangular" height={300} />
        </ChartContainer>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartContainer>
          <Skeleton variant="rectangular" height={300} />
        </ChartContainer>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartContainer>
          <Skeleton variant="rectangular" height={300} />
        </ChartContainer>
      </Grid>
    </Grid>
  );

  // Render error state with options to retry or use sample data
  const renderErrorState = () => (
    <Alert 
      severity="error" 
      variant="filled"
      sx={{ 
        mb: 3,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}
      action={
        <Box>
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleRetry}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Retry
          </Button>
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleAutoRetryToggle}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            {autoRetry ? 'Stop Auto-Retry' : 'Auto-Retry'}
          </Button>
          <Button
            color="inherit"
            size="small"
            onClick={handleUsePlaceholder}
            disabled={loading}
          >
            Use Sample Data
          </Button>
        </Box>
      }
    >
      {error}
    </Alert>
  );

  // Determine if data is available for each section
  const hasTransactionData = stats && stats.transactionStats && stats.transactionStats.length > 0;
  const hasActivityData = stats && stats.activityStats && stats.activityStats.length > 0;
  const hasTopEarnersData = stats && stats.topEarners && stats.topEarners.length > 0;
  const hasTokenStats = stats && stats.tokenStats;
  const hasTimeSeriesData = stats && stats.timeSeriesStats;

  // Calculate total earned and spent for summary metrics
  const calculateTotals = () => {
    if (!hasTransactionData) return { earned: 0, spent: 0 };
    
    const earned = stats.transactionStats
      .filter(stat => stat._id === 'earn' || stat._id === 'transfer_in')
      .reduce((sum, stat) => sum + (stat.totalAmount || 0), 0);
      
    const spent = stats.transactionStats
      .filter(stat => stat._id === 'spend' || stat._id === 'transfer_out')
      .reduce((sum, stat) => sum + (stat.totalAmount || 0), 0);
      
    return { earned, spent };
  };

  // Render token economy summary metrics
  const renderTokenSummary = () => {
    const { earned, spent } = calculateTotals();
    const netFlow = earned - spent;
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  TOTAL EARNED
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {formatNumber(earned)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon fontSize="small" color="success" />
                  <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                    +{formatNumber(earned - spent)} Net
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main
                }}
              >
                <TrendingUpIcon />
              </Box>
            </Box>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  TOTAL SPENT
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {formatNumber(spent)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <SwapVertIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    {(spent / (earned || 1) * 100).toFixed(1)}% of Earned
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                <TrendingDownIcon />
              </Box>
            </Box>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ACTIVE USERS
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {hasTopEarnersData ? formatNumber(stats.topEarners.length * 12) : formatNumber(hasTokenStats ? stats.tokenStats.holders : 0)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon fontSize="small" color="success" />
                  <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                    +12% this {period}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main
                }}
              >
                <PeopleIcon />
              </Box>
            </Box>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  AVG TRANSACTION
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {formatNumber(hasTransactionData ? 
                    Math.round(stats.transactionStats.reduce((sum, stat) => sum + stat.totalAmount, 0) / 
                      stats.transactionStats.reduce((sum, stat) => sum + stat.count, 0)) : 0
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <SwapVertIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    {hasTransactionData ? formatNumber(stats.transactionStats.reduce((sum, stat) => sum + stat.count, 0)) : 0} Transactions
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main
                }}
              >
                <AccountBalanceWalletIcon />
              </Box>
            </Box>
          </MetricCard>
        </Grid>
      </Grid>
    );
  };

  // Render advanced token economy metrics
  const renderTokenMetrics = () => {
    if (!hasTokenStats) return null;
    
    const { tokenStats } = stats;
    
    return (
      <ChartContainer sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">Token Economy Metrics</Typography>
          <Tooltip title="Statistics about the overall token economy in your organization">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {Math.round(tokenStats.circulating / tokenStats.totalSupply * 100)}%
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                CIRCULATION RATE
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatNumber(tokenStats.circulating)} / {formatNumber(tokenStats.totalSupply)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <Typography variant="h5" color="success.main" fontWeight="bold">
                  {formatNumber(tokenStats.holders)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                TOKEN HOLDERS
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {tokenStats.holders > 500 ? 'Healthy Adoption' : 'Growing Adoption'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <Typography variant="h5" color="info.main" fontWeight="bold">
                  {formatNumber(tokenStats.avgBalance)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                AVG BALANCE
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                Per Active User
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <Typography variant="h5" color="warning.main" fontWeight="bold">
                  {(tokenStats.circulating / tokenStats.holders).toFixed(0)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                DISTRIBUTION RATIO
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {(tokenStats.circulating / tokenStats.holders) > 200 ? 'Well Distributed' : 'Concentrated'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ChartContainer>
    );
  };

  // Render time series data charts
  const renderTimeSeriesCharts = () => {
    if (!hasTimeSeriesData) return null;
    
    const { timeSeriesStats } = stats;
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ChartContainer>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Transaction Volume Trend
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Weekly token transaction volume over time
            </Typography>
            
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={timeSeriesStats.transactionVolume}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tickFormatter={value => `${value >= 1000 ? `${(value/1000).toFixed(1)}k` : value}`}
                    tick={{ fontSize: 12 }}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`${formatNumber(value)} tokens`, 'Volume']}
                    labelFormatter={formatDate}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="volume" 
                    stroke={theme.palette.primary.main} 
                    fillOpacity={1} 
                    fill="url(#colorVolume)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ChartContainer>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartContainer>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              User Growth
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Weekly token holder growth
            </Typography>
            
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={timeSeriesStats.userGrowth}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    domain={['dataMin - 50', 'dataMax + 50']}
                    tick={{ fontSize: 12 }}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`${formatNumber(value)} users`, 'Active Users']}
                    labelFormatter={formatDate}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </ChartContainer>
        </Grid>
      </Grid>
    );
  };

  // Render all charts when data is loaded
  const renderDataVisualizations = () => (
    <Box>
      {renderTokenSummary()}
      
      {renderTokenMetrics()}
      
      {renderTimeSeriesCharts()}
      
      <Grid container spacing={3}>
        {hasTransactionData && (
          <Grid item xs={12}>
            <ChartContainer>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Transaction Activity
              </Typography>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={formatTransactionData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    barSize={40}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={value => `${value >= 1000 ? `${(value/1000).toFixed(1)}k` : value}`}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        `${formatNumber(value)} tokens`,
                        name === 'value' ? 'Token Amount' : 'Count'
                      ]} 
                      cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="value" 
                      name="Token Amount" 
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </ChartContainer>
          </Grid>
        )}

        {hasActivityData && (
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Earning Activities Distribution
              </Typography>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatActivityData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      innerRadius={50}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {formatActivityData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => [`${formatNumber(value)} tokens`, 'Amount']} 
                    />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </ChartContainer>
          </Grid>
        )}

        {hasActivityData && (
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Activity Frequency
              </Typography>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={formatActivityData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    layout="vertical"
                    barSize={20}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={120}
                    />
                    <RechartsTooltip 
                      formatter={(value) => [formatNumber(value), 'Activities']} 
                      cursor={{ fill: alpha(theme.palette.secondary.main, 0.05) }}
                    />
                    <Bar 
                      dataKey="count" 
                      name="Number of Activities" 
                      fill={theme.palette.secondary.main}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </ChartContainer>
          </Grid>
        )}

        {hasTopEarnersData && (
          <Grid item xs={12}>
            <ChartContainer>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Top Token Earners
              </Typography>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.topEarners.map(earner => ({
                      name: earner.memberName || 'Anonymous',
                      balance: earner.balance,
                      earned: earner.totalEarned,
                      spent: earner.totalSpent
                    })).sort((a, b) => b.balance - a.balance).slice(0, 10)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    barGap={0}
                    barCategoryGap="15%"
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis 
                      tickFormatter={value => `${value >= 1000 ? `${(value/1000).toFixed(1)}k` : value}`}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        const formatter = new Intl.NumberFormat('en-US');
                        return [formatter.format(value), name.charAt(0).toUpperCase() + name.slice(1)];
                      }}
                      cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                    />
                    <Legend />
                    <Bar dataKey="balance" name="Current Balance" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="earned" name="Total Earned" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="spent" name="Total Spent" fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </ChartContainer>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // Main component render
  return (
    <GradientCard>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography 
              variant="h5" 
              fontWeight="bold"
              sx={{ 
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              Token Economy Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track token circulation, usage patterns and member engagement
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeriodSelector size="small" sx={{ minWidth: 140, mr: 2 }}>
              <InputLabel id="period-select-label">Time Period</InputLabel>
              <Select
                labelId="period-select-label"
                value={period}
                label="Time Period"
                onChange={(e) => setPeriod(e.target.value)}
                disabled={loading}
                startAdornment={
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="quarter">Last Quarter</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
              </Select>
            </PeriodSelector>
            
            <StatButton
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={loading}
              size="medium"
            >
              Refresh
            </StatButton>
          </Box>
        </Box>

        {usePlaceholder && (
          <Alert 
            severity="info" 
            variant="outlined"
            icon={<InfoIcon />}
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              borderWidth: 2,
              backgroundColor: alpha(theme.palette.info.main, 0.05)
            }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRetry}
              >
                Try Actual Data
              </Button>
            }
          >
            Currently showing sample data for demonstration purposes.
          </Alert>
        )}

        {error && renderErrorState()}
        
        {loading ? renderLoadingState() : (
          <>
            {stats ? (
              <>
                {renderDataVisualizations()}
              </>
            ) : renderNoDataPlaceholder()}
          </>
        )}
      </CardContent>
      
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Box>
            {loadingTime > 8 && loading && (
              <Button 
                color="primary" 
                size="small" 
                onClick={handleUsePlaceholder} 
                sx={{ mr: 2 }}
              >
                View Sample
              </Button>
            )}
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleToastClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      />
    </GradientCard>
  );
};

export default TokenStatistics;